import { KeycloakContextHolder } from '../../components/context/KeycloakContext';
import { useContext, useEffect, useReducer } from 'react';
import { AuthManager } from '../../components/context/AuthManager';
import { AuthTokenData } from '../../components/context/AuthTokenData';
import { LoaderContext } from '../../components/context/LoaderContext';
import { AppShopContext } from '../../components/context/AppShopContext';

const env = process.env.REACT_APP_BUILD_ENV;
const keycloakLibUrl = `https://login.${
  env === 'prod' ? 'prod' : 'dev'
}.zooplus.net/auth/js/keycloak.js`;

const supportedRealms = ['zooplus', 'bitiba'];

export function useKeycloakAuthContext(): KeycloakContextHolder {
  const { domain, realm } = useContext(AppShopContext);
  const loaderContext = useContext(LoaderContext);

  if (!domain || !realm) {
    console.log(
      'Not triggering authentication because either {domain} or {realm} param is missing.',
    );
    return KeycloakContextHolder.create({} as AuthTokenData);
  }

  if (!supportedRealms.some((supportedRealm) => supportedRealm === realm)) {
    console.log(`Not triggering authentication for unsupported ${realm}.`);
    return KeycloakContextHolder.create({} as AuthTokenData);
  }

  let authManager: AuthManager;

  const loadKeycloak = (callback: Function) => {
    const isTestEnv = process.env.REACT_APP_BUILD_ENV === 'test';
    const existingScript = isTestEnv || document.getElementById('kc');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = keycloakLibUrl;
      script.id = 'kc';
      document.body.appendChild(script);
      script.onload = () => {
        if (callback) {
          callback();
        }
      };
    }
    if (existingScript && callback) {
      callback();
    }
  };

  const [keycloakContext, dispatchKeycloakContextChange] = useReducer((state: any, action: any) => {
    if (action.payload) {
      return KeycloakContextHolder.create(action.payload);
    }

    return state;
  }, KeycloakContextHolder.create());

  useEffect(() => {
    loaderContext.requestLoaderShow('keycloak');
    loadKeycloak(() => {
      initializeAuthManager(window.Keycloak);
      loaderContext.requestLoaderHide('keycloak');
    });
    hideLoaderInCaseOfSSOFailure();
  }, []);

  function hideLoaderInCaseOfSSOFailure() {
    setTimeout(() => loaderContext.requestLoaderHide('auth-init'), 5000);
  }

  function initializeAuthManager(keycloakInitializer: Function) {
    loaderContext.requestLoaderShow('auth-init');
    authManager = new AuthManager(keycloakInitializer, domain, realm)
      .onAuthenticated(onAuthenticated)
      .onNotAuthenticated(onNotAuthenticated)
      .onAuthenticationFailure(onAuthFailure);
    authManager.initKeycloak();
  }

  const onAuthenticated = (tokenData: AuthTokenData) => {
    dispatchKeycloakContextChange({ payload: tokenData });
    loaderContext.requestLoaderHide('auth-init');
  };

  const onNotAuthenticated = () => {
    console.log('Authentication unsuccessful.');
    dispatchKeycloakContextChange({ payload: {} });
    loaderContext.requestLoaderHide('auth-init');
  };

  const onAuthFailure = (reason: string) => {
    console.log('Authentication failed.', reason ? ` Reason: ${reason}` : '');
    dispatchKeycloakContextChange({ payload: {} });
    loaderContext.requestLoaderHide('auth-init');
  };

  return keycloakContext;
}
